import { Helmet, HelmetProvider } from "@dr.pogodin/react-helmet";
import { observer } from "mobx-react-lite";
import { usePostHog } from "posthog-js/react";
import { lazy, Suspense, useEffect, useRef } from "react";
import "react-loading-skeleton/dist/skeleton.css";
import { Redirect, Route, Switch, useLocation } from "wouter";
import JoyOfFoodCampaignPage from "./account/promotions/JoyOfFoodCampaignPage";
import { ENV_NAME, POSTHOG_ENABLED } from "./common/environment";
import { useAppStartup } from "./common/hooks/useAppStartup";
import { useStuckStartupReporting } from "./common/hooks/useStuckStartupReporting";
import { logger } from "./common/logger";
import useMediaQuery from "./common/useMediaQuery";
import { usePageViewTracking } from "./common/usePageViewTracking";
import { desktopBreakpoint } from "./common/utils";
import MainColumn from "./components/common/MainColumn";
import FlyingProductOrItem from "./components/FlyingProductOrItem";
import Loading from "./components/Loading";
import PreprodInfo from "./components/PreprodInfo";
import ScrollToTop from "./components/ScrollToTop";
import SessionChecker from "./components/SessionChecker";
import TopBarWarnings from "./components/TopBarWarnings";
import VurbisPunchoutScript from "./components/VurbisPunchoutScript";
import Footer from "./footer/Footer";
import DesktopHeader from "./header/desktop/DesktopHeader";
import MobileHeader from "./header/mobile/MobileHeader";
import { isBrowserMockRuntimeEnabled } from "./mocks/browserMockRuntime";
import authStore from "./stores/auth/authStore";
import loginState from "./stores/auth/loginState";
import barcodeStore from "./stores/barcodeStore";
import { startCartAvailabilityExperiment } from "./stores/cart/cartAvailabilityExperiment";
import cartStore from "./stores/cart/cartStore";
import deliveryDatesStore from "./stores/deliveryDates/deliveryDatesStore";
import featuresStore from "./stores/features/featuresStore";
import posthogStore from "./stores/posthogStore";
import productStore from "./stores/product/productStore";
import toastStore from "./stores/toastStore";
import uiStore from "./stores/uiStore";
import theme from "./themes/theme";

const BarcodeScanner = lazy(() => import("./components/barcodes/BarcodeScanner"));
const CartSendingOrderModal = lazy(() => import("./components/CartSendingOrderModal"));
const Cart = lazy(() => import("./pages/cart/Cart"));
const ChangeDeliveryDateModal = lazy(() => import("./components/ChangeDeliveryDateModal"));
const CustomerRoutes = lazy(() => import("./account/CustomerRoutes"));
const ExpiredDeadlineModal = lazy(() => import("./components/ExpiredDeadlineModal"));
const ForcedLogoutModal = lazy(() => import("./components/login/ForcedLogoutModal"));
const ProductListPage = lazy(() => import("./pages/ProductListPage"));
const Home = lazy(() => import("./pages/Home"));
const LoginModal = lazy(() => import("./components/login/LoginModal"));
const LoginProgress = lazy(() => import("./header/LoginProgress"));
const LoggerSettingsModal = lazy(() => import("./components/LoggerSettingsModal"));
const SearchResult = lazy(() => import("./pages/SearchResult"));
const ProductDetailsPage = lazy(() => import("./pages/product-details/ProductDetailsPage"));
const SubscriptionConfirmation = lazy(() => import("./pages/SubscriptionConfirmation"));
const PickupConfirmation = lazy(() => import("./pages/PickupConfirmation"));
const CmsContentPreview = lazy(() => import("./components/cms/CmsContentPreview"));
const CmsContent = lazy(() => import("./components/cms/CmsContent"));
const CustomerServicePage = lazy(() => import("./pages/CustomerServicePage"));
const SwitchCompanyPunchoutPage = lazy(() => import("./pages/punchout/SwitchCompanyPunchoutPage"));
const AdventCalendar = lazy(() => import("./pages/AdventCalendarPage"));
const CookiePage = lazy(() => import("./pages/CookiePage"));
const ComponentShowcasePage = lazy(() => import("./pages/ComponentShowcasePage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ComplianceDocumentsPage = lazy(() => import("./pages/compliance-documents/ComplianceDocumentsPage"));
const SwitchCompanyModal = lazy(() => import("./components/SwitchCompanyModal"));
const ToastDisplay = lazy(() => import("./components/ToastDisplay"));
const OrderContainer = lazy(() => import("./account/orders/order-details/OrderContainer"));
const LegacyOrderConfirmation = lazy(() => import("./pages/OrderConfirmation"));

const OrderConfirmationRoute = (props: { params: { id: string } }) => {
   if (featuresStore.newOrderFlowEnabled) {
      return <OrderContainer />;
   }

   const query = typeof window === "undefined" ? "" : window.location.search;
   return <Redirect to={`/customer/orders/${props.params.id}${query}`} replace />;
};

const loginStatesWithModalVisible = new Set([
   "VERIFY_SESSION",
   "LOGGING_IN",
   "COMPANY_SELECTED",
   "FEATURES_LOADING",
   "DELIVERY_DATES_LOADING",
   "ASSORTMENT_LOADING",
   "NOTIFICATIONS_LOADING"
]);

const App = () => {
   const [location, redirectTo] = useLocation();
   const posthog = usePostHog();

   useAppStartup(location, redirectTo);
   useStuckStartupReporting();

   useEffect(() => {
      // Note: usePostHog() returns a default PostHog instance even without PostHogProvider,
      // so we must check POSTHOG_ENABLED flag instead of checking if posthog exists
      if (POSTHOG_ENABLED && !isBrowserMockRuntimeEnabled()) {
         posthogStore.init(posthog);
      }
   }, [posthog]);

   // DSP-2628: the pilot reads the PostHog flag on each check, so it does nothing until PostHog is ready
   useEffect(() => startCartAvailabilityExperiment(), []);

   usePageViewTracking();

   const prevLocationRef = useRef<string | null>(null);
   const isJulekalender = (path: string) => /^\/kampanjer\/julekalender(\/|$)/.test(path);

   const waitFor = (
      condition: () => boolean,
      callback: () => void,
      options = { interval: 100, maxAttempts: 50 }
   ): (() => void) => {
      let attempts = 0;
      const checkInterval = setInterval(() => {
         attempts++;
         if (condition()) {
            clearInterval(checkInterval);
            callback();
         } else if (attempts >= options.maxAttempts) {
            clearInterval(checkInterval);
            logger.warn("ui", "waitFor: Condition not met within timeout");
         }
      }, options.interval);
      return () => clearInterval(checkInterval);
   };

   useEffect(() => {
      if (typeof window === "undefined") return;
      if (isBrowserMockRuntimeEnabled()) return;

      const wasJulekalender = prevLocationRef.current ? isJulekalender(prevLocationRef.current) : false;
      const isNowJulekalender = isJulekalender(location);
      if (prevLocationRef.current !== null && wasJulekalender === isNowJulekalender) {
         return;
      }

      prevLocationRef.current = location;

      const updateChatVisibility = () => {
         if (isNowJulekalender) {
            window.LC_API.hide_chat_window();
         } else if (wasJulekalender) {
            window.LC_API.minimize_chat_window();
         }
      };

      const isLCAPIReady = () =>
         window.LC_API && typeof window.LC_API.hide_chat_window === "function" && window.LC_API.is_loaded();
      let cleanup: (() => void) | undefined;

      if (isLCAPIReady()) {
         updateChatVisibility();
      } else {
         cleanup = waitFor(isLCAPIReady, updateChatVisibility);
      }
      return cleanup;
   }, [location]);

   const isDesktop = useMediaQuery(desktopBreakpoint);
   const isPunchoutCompanySelect = authStore.isPunchoutSession() && loginState.is("COMPANY_SELECTION");

   const showLoginModal = loginState.is("LOGIN_MODAL") || loginState.is("CHECKING_LOGIN");
   const showLoginProgress = loginStatesWithModalVisible.has(loginState.currentState);
   const showChangeDeliveryDateModal = deliveryDatesStore.changingDeliveryDate && !loginState.is("DELIVERY_DATES_LOADING");
   const showSwitchCompanyModal = loginState.is("COMPANY_SELECTION");
   const showForcedLogoutModal = loginState.is("FORCED_LOGOUT");
   const showLoggerSettingsModal = uiStore.showLoggerModal;
   const showCartSendingOrderModal = cartStore.showSendingOrderModal;
   const showToastDisplay = toastStore.toasts.length > 0;

   return (
      <HelmetProvider>
         <Helmet>
            <title>{theme.siteName}</title>
         </Helmet>
         {isPunchoutCompanySelect && (
            <Suspense fallback={<Loading />}>
               <SwitchCompanyPunchoutPage />
            </Suspense>
         )}
         {!isPunchoutCompanySelect && (
            <>
               {ENV_NAME !== "prod" && <PreprodInfo />}
               <TopBarWarnings />
               <header className="relative z-30 print:hidden">
                  <ScrollToTop />
                  {isDesktop ? <DesktopHeader /> : <MobileHeader />}
               </header>
               <MainColumn as="main">
                  <div className="min-h-[90vh] w-full pb-20 pt-6 print:text-[80%]">
                     <Suspense fallback={<Loading />}>
                        <Switch>
                           <Route path={/^(?<fullUrl>\/)$/} component={Home} />
                           <Route path="/home">
                              <Redirect to="/" replace />
                           </Route>
                           {ENV_NAME !== "prod" && <Route path="/components" component={ComponentShowcasePage} />}
                           <Route path="/produkter/nyheter.html">
                              <Redirect to="/produkter.html?shortcut=nyheter" replace />
                           </Route>
                           {featuresStore.productNavigationAvailable && (
                              <Route path="/produkter/:cat2/:cat3">{(p) => <ProductListPage {...p} />}</Route>
                           )}
                           {featuresStore.productNavigationAvailable && (
                              <Route path="/produkter/:cat2">{(p) => <ProductListPage {...p} />}</Route>
                           )}
                           {featuresStore.productNavigationAvailable && (
                              <Route path="/produkter.html">{() => <ProductListPage />}</Route>
                           )}
                           <Route path="/customer/*" component={CustomerRoutes} />
                           <Route path="/checkout/cart" component={Cart} />
                           <Route path="/checkout/confirmation/subscription" component={SubscriptionConfirmation} />
                           <Route path="/checkout/confirmation/pickup" component={PickupConfirmation} />
                           {!featuresStore.newOrderFlowEnabled && (
                              <Route path="checkout/confirmation" component={LegacyOrderConfirmation} />
                           )}
                           <Route path="/checkout/confirmation/:id" component={OrderConfirmationRoute} />
                           <Route path="/search/:type?" component={SearchResult} />
                           <Route path="/produkt/:slug">{(p) => <ProductDetailsPage {...p} />}</Route>
                           <Route
                              path="/kontakt"
                              component={theme.storeId === "tinehandel" ? CustomerServicePage : ContactPage}
                           />
                           {theme.storeId === "tinehandel" && <Route path="/dokumenter" component={ComplianceDocumentsPage} />}
                           <Route path="/kampanjer/arets-matglede-rapport" component={JoyOfFoodCampaignPage} />
                           <Route path="/kampanjer/julekalender" component={AdventCalendar} />
                           <Route path="/informasjonskapsler" component={CookiePage} />
                           <Route path={/^\/_preview\/(?<fullUrl>.*)$/} component={CmsContentPreview} />
                           <Route path={/^\/(?<fullUrl>.*)$/} component={CmsContent} />
                        </Switch>
                     </Suspense>
                  </div>
                  <div id="quicksearch-root" />

                  {uiStore.planes.map((p) => (
                     <FlyingProductOrItem key={p.animationId} plane={p} />
                  ))}
                  {featuresStore.barcodeScannerEnabled && barcodeStore.modalVisible && (
                     <Suspense fallback={<Loading modal />}>
                        <BarcodeScanner />
                     </Suspense>
                  )}

                  {showChangeDeliveryDateModal && (
                     <Suspense fallback={null}>
                        <ChangeDeliveryDateModal />
                     </Suspense>
                  )}
                  {showSwitchCompanyModal && (
                     <Suspense fallback={null}>
                        <SwitchCompanyModal />
                     </Suspense>
                  )}
                  <Suspense fallback={null}>
                     <ExpiredDeadlineModal />
                  </Suspense>
                  {showCartSendingOrderModal && (
                     <Suspense fallback={null}>
                        <CartSendingOrderModal />
                     </Suspense>
                  )}
               </MainColumn>
               <footer className="bg-headerFooterBackground [&>*]:text-headerFooterText">
                  <Footer />
               </footer>
            </>
         )}
         {showToastDisplay && (
            <Suspense fallback={null}>
               <ToastDisplay />
            </Suspense>
         )}
         {showLoginModal && (
            <Suspense fallback={null}>
               <LoginModal />
            </Suspense>
         )}
         {showLoginProgress && (
            <Suspense fallback={null}>
               <LoginProgress />
            </Suspense>
         )}
         {showForcedLogoutModal && (
            <Suspense fallback={null}>
               <ForcedLogoutModal />
            </Suspense>
         )}
         <SessionChecker
            onChange={(vis) => {
               authStore.verifyStillLoggedIn(vis);
               productStore.retryAssortmentAfterFallback();
            }}
         />
         <VurbisPunchoutScript />
         {showLoggerSettingsModal && (
            <Suspense fallback={null}>
               <LoggerSettingsModal />
            </Suspense>
         )}
      </HelmetProvider>
   );
};

export default observer(App);
